<template>
    <div class="relative">

        <!-- 🔄 LOADER -->
        <div v-if="loading" class="absolute inset-0 bg-white bg-opacity-70 flex items-center justify-center z-10">
            <i class="fas fa-spinner fa-spin fa-2x text-gray-600"></i>
        </div>

        <!-- 🔧 HEADER CALENDRIER -->
        <div class="flex items-center gap-2 mb-3">
            <button @click="previousDay" class="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200">
                <i class="fas fa-chevron-left"></i>
            </button>

            <button @click="today" class="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200">
                <i class="fas fa-calendar-day"></i>
            </button>

            <button @click="nextDay" class="px-3 py-2 rounded bg-gray-100 hover:bg-gray-200">
                <i class="fas fa-chevron-right"></i>
            </button>

            <div class="ml-4 font-semibold">{{ prettyDate }}</div>

            <!-- 👥 Filtre techniciens -->
            <div class="ml-auto relative">
                <button @click="toggleDropdown"
                    class="flex items-center gap-2 px-3 py-2 border rounded bg-white hover:bg-gray-50 text-sm">
                    <i class="fas fa-users text-gray-600"></i>
                    <span>{{ dropdownLabel }}</span>
                    <i class="fas" :class="showDropdown ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                </button>

                <div v-if="showDropdown"
                    class="absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg z-50 p-2">

                    <div class="flex items-center mb-2">
                        <input type="checkbox" id="select-all" v-model="allSelected" @change="toggleAll" class="mr-2">
                        <label for="select-all" class="font-medium text-sm cursor-pointer">Tout sélectionner</label>
                    </div>

                    <div class="max-h-48 overflow-y-auto">
                        <div v-for="r in resources" :key="r.id" class="flex items-center py-1">
                            <input type="checkbox" :id="'emp-' + r.id" v-model="selectedResources" :value="r.id"
                                class="mr-2 cursor-pointer">
                            <label :for="'emp-' + r.id" class="text-sm cursor-pointer">{{ r.name }}</label>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <!-- 🗓️ CALENDRIER -->
        <div ref="scheduler" id="scheduler" style="height:650px;"></div>

        <!-- Intervention à planifier -->
        <!-- FILE D'ATTENTE DE PLANIFICATION -->
        <div v-if="planningQueue.length" class="mt-4 p-4 bg-orange-50 border-l-4 border-orange-400 rounded">

            <h3 class="font-semibold mb-3 text-orange-700 flex items-center gap-2">
                <i class="fas fa-exclamation-triangle"></i> Intervention à planifier
            </h3>

            <div 
                v-for="int in planningQueue" 
                :key="int.id"
                :id="'queue-item-' + int.id"
                class="p-4 border rounded bg-white shadow cursor-move hover:bg-gray-50 transition"
            >
                <div class="font-semibold">{{ int.type }}</div>
                <div class="text-sm text-gray-600">{{ int.client }}</div>
                <div class="text-sm text-gray-500">{{ int.adresse }}</div>
                <div class="mt-1">
                    <span class="text-xs text-gray-400 bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                        <i class="fas fa-arrows-alt"></i> Glisser sur un technicien
                    </span>
                </div>
            </div>
        </div>


    </div>
</template>

<script>
import { DayPilot } from "daypilot-pro-javascript";
import moment from "moment";

export default {
    name: "CalendarIntervention",

    props: {
        defaultDate: {
            type: String,
            default: () => new DayPilot.Date().toString("yyyy-MM-dd")
        }
    },

    data() {
        return {
            scheduler: null,
            currentDate: this.defaultDate,

            loading: false,

            resources: [],
            selectedResources: [],
            events: [],

            planningQueue: [],
            dragElementIds: [],

            showDropdown: false
        };
    },

    computed: {
        prettyDate() {
            return new DayPilot.Date(this.currentDate).toString("dddd dd MMMM yyyy", "fr-fr");
        },

        allSelected() {
            return this.selectedResources.length === this.resources.length;
        },

        dropdownLabel() {
            if (this.allSelected) return "Tous les employés";
            if (this.selectedResources.length === 0) return "Aucun sélectionné";
            if (this.selectedResources.length === 1) {
                const emp = this.resources.find(r => r.id === this.selectedResources[0]);
                return emp?.name || "1 employé";
            }
            return `${this.selectedResources.length} sélectionnés`;
        }
    },

    methods: {
        /* ---------------------------------------------
         * 🔄 Navigation
         * ------------------------------------------- */
        nextDay() {
            this.currentDate = new DayPilot.Date(this.currentDate).addDays(1).toString("yyyy-MM-dd");
            this.refreshCalendar();
        },
        previousDay() {
            this.currentDate = new DayPilot.Date(this.currentDate).addDays(-1).toString("yyyy-MM-dd");
            this.refreshCalendar();
        },
        today() {
            this.currentDate = new DayPilot.Date().toString("yyyy-MM-dd");
            this.refreshCalendar();
        },

        /* ---------------------------------------------
         * 👥 Gestion techniciens
         * ------------------------------------------- */
        toggleDropdown() {
            this.showDropdown = !this.showDropdown;
        },

        toggleAll() {
            this.selectedResources = this.allSelected
                ? []
                : this.resources.map(r => r.id);
            this.applyResourceFilter();
        },

        applyResourceFilter() {
            if (!this.scheduler) return;

            const filtered = this.resources.filter(r => this.selectedResources.includes(r.id));
            this.scheduler.update({ resources: filtered });
        },

        /* ---------------------------------------------
         * 🗓️ Initialisation calendrier
         * ------------------------------------------- */
        initScheduler() {
            // Au moins un technicien sélectionné
            if (this.selectedResources.length === 0) {
                this.scheduler.update({ resources: [] });
                return;
            }
            const config = {
                startDate: this.currentDate,
                days: 1,
                scale: "CellDuration",
                cellDuration: 10,
                cellWidth: 60,

                businessBeginsHour: 8,
                businessEndsHour: 19,
                showNonBusiness: false,
                businessWeekends: true,
                allowEventOverlap: false,
                durationBarVisible: true,
                eventArrangement: "SideBySide",

                timeHeaders: [
                    { groupBy: "Hour", format: "HH" },
                    { groupBy: "Cell", format: "mm" },
                ],

                eventMoveHandling: "Update",
                eventResizeHandling: "Disabled",
                eventClickHandling: "Select",
                eventReceiveHandling: "Update",

                resources: this.resources,
                events: this.events,

                onEventClicked: args => {
                    this.$emit("view", args.e.data.id);
                },

                onEventReceive: async (args) => {
                    const intId = args.e.data.id;

                    const payload = {
                        date_intervention: args.start.toString("yyyy-MM-dd"),
                        heure_debut: args.start.toString("HH:mm:ss"),
                        heure_fin: args.end.toString("HH:mm:ss"),
                        technicien_id: args.resource
                    };

                    try {
                        await this.$request.post(`/interventions/${intId}/planifier`, payload);

                        // Remove from queue
                        this.planningQueue = [];

                        // Reload day events
                        await this.fetchInterventions();
                        this.scheduler.update({ events: this.events });

                        this.$toast.success("Intervention planifiée !");
                        this.$eventHub.$emit("refresh-intervention-table");

                    } catch (e) {
                        this.$toast.error("Erreur planification");
                        args.preventDefault();
                    }
                },

                onEventMoved: async (args) => {
                    if (confirm("Confirmer le déplacement de l'intervention à " + args.newStart.toString("HH:mm") + " ?") === false) {
                        args.preventDefault();
                        return;
                    }
                    const id = args.e.data.id;

                    const payload = {
                        date_intervention: args.newStart.toString("yyyy-MM-dd"),
                        heure_debut: args.newStart.toString("HH:mm:ss"),
                        heure_fin: args.newEnd.toString("HH:mm:ss"),
                        technicien_id: args.newResource
                    };

                    try {
                        // 🔥 Appel API pour enregistrer
                        await this.$request.post(`/interventions/${id}/planifier`, payload);

                        // 🔄 Mise à jour locale de l'événement
                        const ev = this.events.find(e => e.id === id);
                        if (ev) {
                            ev.start = args.newStart;
                            ev.end = args.newEnd;
                            ev.resource = args.newResource;
                        }

                        // 🔁 Mise à jour visuelle du scheduler
                        this.scheduler.update({ events: this.events });

                        // 📣 Notifier le parent si besoin
                        this.$emit("updated", { id, ...payload });

                        if (this.planningQueue.length && this.planningQueue[0].id === id) {
                            this.planningQueue = [];
                            this.$toast.success("Intervention planifiée");
                            this.$eventHub.$emit("refresh-intervention-table");
                        } else {
                            this.$toast.success("Intervention déplacée");
                        }

                    } catch (e) {
                        console.error("Erreur replanification :", e);
                        this.$toast.error("Impossible de déplacer l'intervention.");

                        // ❌ Annuler le déplacement si erreur
                        args.preventDefault();
                    }
                }
            };

            if (!this.scheduler) {
                this.scheduler = new DayPilot.Scheduler(this.$refs.scheduler, config);
                this.scheduler.init();
            } else {
                this.scheduler.update(config);
            }
        },

        /* ---------------------------------------------
         * 🔄 Rechargement
         * ------------------------------------------- */
        async refreshCalendar() {
            this.loading = true;

            await this.fetchInterventions();
            this.initScheduler();

            this.loading = false;
        },

        /* ---------------------------------------------
         * API CALLS
         * ------------------------------------------- */
        async fetchTechniciens() {
            const response = await this.$request.get("/techniciens");

            this.resources = response.map(t => ({
                id: t.id.toString(),
                name: t.prenom + " " + t.nom
            }));

            this.selectedResources = this.resources.map(r => r.id);
        },

        async fetchInterventions() {
            const response = await this.$request.post("/interventions/planned", {
                date: this.currentDate
            });

            this.events = response.map(int => ({
                id: String(int.id),
                text: int.intervention_type.nom + " - " + int.client.nom,
                start: new DayPilot.Date(int.date_intervention + " " + int.heure_debut),
                end: new DayPilot.Date(int.date_intervention + " " + int.heure_fin),
                resource: String(int.technicien.id),
                data: int,
                fontColor: "#4781de"
            }));
        },

        makeQueueDraggable() {
            // Nettoyage
            this.dragElementIds.forEach(id => {
                const el = document.getElementById(id);
                if (el) el.remove();
            });
            this.dragElementIds = [];

            // Générer les éléments draggables
            this.planningQueue.forEach((int) => {
                const el = document.getElementById("queue-item-" + int.id);
                if (!el) return;

                DayPilot.Scheduler.makeDraggable({
                    element: el,
                    data: {
                        id: int.id,
                        text: int.type + " - " + int.client,
                        duration: DayPilot.Duration.hours( int.duree_estimee / 60 )
                    },
                    externalCssClass: "external-task-dragging"
                });

                this.dragElementIds.push("queue-item-" + int.id);
            });
        }

    },

    async mounted() {
        await this.fetchTechniciens();
        this.refreshCalendar();

        this.$eventHub.$on("PLANIFIER_INTERVENTION", (intervention) => {
            // Toujours une seule carte
            this.planningQueue = [ intervention ];

            this.$nextTick(() => {
                this.makeQueueDraggable();
            });
        });
    }
};
</script>

<style scoped>
#scheduler {
    background: white;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
}
</style>
